import Link from "next/link"
import type { LinkProps } from "next/link"
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react"

import { cn } from "@/lib/utils"

export type MarketingActionVariant = "primary" | "secondary" | "ghost" | "accent"
export type MarketingActionSize = "sm" | "md" | "lg" | "nav"
export type MarketingIconActionSize = "sm" | "md" | "lg"

const VARIANT_CLASS: Record<MarketingActionVariant, string> = {
  primary:
    "border-[var(--color-rule-strong)] bg-[var(--color-paper-high)] text-[var(--color-ink)] surface-1 hover:border-[var(--color-vermilion)] hover:text-[var(--color-vermilion)]",
  accent:
    "border-[var(--color-vermilion)] bg-[var(--color-vermilion)] text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.3),var(--glass-shadow-2)] hover:border-[var(--color-oxblood)] hover:bg-[var(--color-oxblood)] hover:text-white",
  secondary:
    "border-[var(--color-rule-strong)] bg-transparent text-[var(--color-ink)] hover:border-[var(--color-vermilion)] hover:text-[var(--color-vermilion)]",
  ghost:
    "border-transparent bg-transparent text-[var(--color-ink-muted)] hover:border-[var(--color-rule-strong)] hover:bg-[var(--color-paper-deep)] hover:text-[var(--color-ink)]",
}

const SIZE_CLASS: Record<MarketingActionSize, string> = {
  sm: "min-h-10 px-4 py-2 text-[0.86rem]",
  md: "min-h-11 px-5 py-2.5 text-[0.92rem]",
  lg: "min-h-12 px-6 py-3 text-[0.95rem]",
  nav: "min-h-9 px-4 py-2 text-[0.82rem]",
}

const ICON_SIZE_CLASS: Record<MarketingIconActionSize, string> = {
  sm: "h-9 w-9",
  md: "h-10 w-10",
  lg: "h-11 w-11",
}

const ICON_VARIANT_CLASS: Record<MarketingActionVariant, string> = {
  primary:
    "border-[var(--color-rule-strong)] bg-[var(--color-paper-high)] text-[var(--color-ink-muted)] shadow-[0_1px_0_rgba(18,18,18,0.035)] hover:border-[var(--color-vermilion)] hover:text-[var(--color-vermilion)]",
  accent:
    "border-[var(--color-vermilion)] bg-[var(--color-vermilion)] text-white shadow-[0_10px_28px_-25px_rgba(18,18,18,0.2)] hover:border-[var(--color-oxblood)] hover:bg-[var(--color-oxblood)] hover:text-white",
  secondary:
    "border-[var(--color-rule)] bg-[var(--color-paper-high)] text-[var(--color-ink-muted)] hover:border-[var(--color-vermilion)] hover:text-[var(--color-vermilion)]",
  ghost:
    "border-transparent bg-transparent text-[var(--color-ink-muted)] hover:bg-[var(--color-paper-deep)] hover:text-[var(--color-ink)]",
}

export function marketingActionClass({
  variant = "primary",
  size = "md",
  className,
}: {
  variant?: MarketingActionVariant
  size?: MarketingActionSize
  className?: string
} = {}) {
  return cn(
    "inline-flex items-center justify-center gap-2 rounded-[var(--radius-sm)] border font-semibold outline-none transition-[transform,color,background-color,border-color,box-shadow] duration-200 ease-[var(--ease-out-soft)] focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-paper)] active:translate-y-[0.5px] disabled:cursor-not-allowed disabled:opacity-60",
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    className,
  )
}

export function marketingIconActionClass({
  variant = "primary",
  size = "md",
  className,
}: {
  variant?: MarketingActionVariant
  size?: MarketingIconActionSize
  className?: string
} = {}) {
  return cn(
    "inline-flex shrink-0 items-center justify-center rounded-[var(--radius-sm)] border outline-none transition-[transform,color,background-color,border-color,box-shadow] duration-200 ease-[var(--ease-out-soft)] focus-visible:ring-2 focus-visible:ring-[var(--color-vermilion)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-paper)] active:translate-y-[0.5px] disabled:cursor-not-allowed disabled:opacity-60",
    ICON_VARIANT_CLASS[variant],
    ICON_SIZE_CLASS[size],
    className,
  )
}

type MarketingLinkProps = LinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps | "className"> & {
    children: ReactNode
    className?: string
    size?: MarketingActionSize
    variant?: MarketingActionVariant
  }

export function MarketingLink({
  children,
  className,
  size,
  variant,
  ...props
}: MarketingLinkProps) {
  return (
    <Link className={marketingActionClass({ variant, size, className })} {...props}>
      {children}
    </Link>
  )
}

type MarketingButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: MarketingActionSize
  variant?: MarketingActionVariant
}

export function MarketingButton({
  children,
  className,
  size,
  variant,
  ...props
}: MarketingButtonProps) {
  return (
    <button
      className={marketingActionClass({ variant, size, className })}
      {...props}
    >
      {children}
    </button>
  )
}
